import { Controller } from "@hotwired/stimulus"
import { heicTo, isHeic } from "heic-to"

export default class extends Controller {
  static targets = ["fileInputsContainer", "existingPhotosContainer"]
  static values = {
    maxPhotos: { type: Number, default: 10 }
  }

  connect() {
    this.photoCount = 0
    this.activeObjectUrls = []
    this.addPhotoInput()
  }

  disconnect() {
    // Cleanup all object URLs
    this.activeObjectUrls.forEach(url => URL.revokeObjectURL(url))
    this.activeObjectUrls = []
  }

  addPhotoInput() {
    if (this.photoCount >= this.maxPhotosValue) return

    const wrapper = document.createElement('div')
    wrapper.className = 'photo-upload-item mb-3'
    wrapper.dataset.photoIndex = this.photoCount
    
    wrapper.innerHTML = `
      <div class="photo-selector border rounded p-3 text-center" style="cursor: pointer; background: #f8f9fa;">
        <i class="fa-solid fa-image fa-2x text-muted mb-2"></i>
        <p class="mb-0 text-muted">${this.selectPhotoText}</p>
        <input type="file" 
               name="nomination[photos][]" 
               accept="image/*" 
               class="d-none"
               data-action="change->nomination-photos-form#handlePhotoSelect"
               data-photo-index="${this.photoCount}">
      </div>
      <div class="photo-preview d-none mt-2"></div>
    `

    this.fileInputsContainerTarget.appendChild(wrapper)
    
    // Add click handler to photo selector
    const selector = wrapper.querySelector('.photo-selector')
    const input = wrapper.querySelector('input[type="file"]')
    selector.addEventListener('click', () => input.click())
    
    this.photoCount++
  }

  async handlePhotoSelect(event) {
    const input = event.target
    const file = input.files[0]
    if (!file) return

    const photoIndex = input.dataset.photoIndex
    const wrapper = this.fileInputsContainerTarget.querySelector(`[data-photo-index="${photoIndex}"]`)
    const selector = wrapper.querySelector('.photo-selector')
    const preview = wrapper.querySelector('.photo-preview')

    // Hide selector, show preview
    selector.classList.add('d-none')
    preview.classList.remove('d-none')

    // Show image preview
    await this.showImagePreview(file, preview, input)

    // Add another photo input if under limit
    if (this.photoCount < this.maxPhotosValue) {
      this.addPhotoInput()
    }
  }

  async showImagePreview(file, preview, input) {
    let blobForPreview = file
    let displayName = file.name

    try {
      // Check if HEIC and convert to JPEG for preview
      if (await this.shouldConvertHeic(file)) {
        // Show converting message
        preview.innerHTML = `
          <div class="alert alert-info">
            <i class="fa-solid fa-spinner fa-spin me-2"></i>
            ${this.convertingText}
          </div>
        `
        
        blobForPreview = await this.convertHeicToJpeg(file)
        displayName = this.convertedFileName(file.name)
      }

      const imageUrl = URL.createObjectURL(blobForPreview)
      this.activeObjectUrls.push(imageUrl)

      preview.innerHTML = `
        <div class="card">
          <div class="card-body p-2">
            <div class="row align-items-center">
              <div class="col-auto">
                <img src="${imageUrl}" 
                     class="img-thumbnail cursor-pointer" 
                     style="max-height: 120px; max-width: 120px; object-fit: cover;"
                     data-bs-toggle="modal"
                     data-bs-target="#mediaModal"
                     data-media-url="${imageUrl}"
                     data-media-type="image">
              </div>
              <div class="col">
                <p class="mb-1 small"><strong>${this.escapeHTML(this.truncateFilename(displayName, 30))}</strong></p>
                <p class="mb-0 text-muted small">${this.formatFileSize(blobForPreview.size)}</p>
              </div>
              <div class="col-auto">
                <button type="button" 
                        class="btn btn-sm btn-outline-danger" 
                        data-action="click->nomination-photos-form#removePhoto"
                        data-photo-index="${input.dataset.photoIndex}">
                  <i class="fa-solid fa-times"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      `
    } catch (error) {
      console.error('Image preview error:', error)
      preview.innerHTML = `
        <div class="alert alert-warning">
          <i class="fa-solid fa-exclamation-triangle me-2"></i>
          ${this.conversionErrorText}
        </div>
      `
    }
  }

  async shouldConvertHeic(file) {
    try {
      if (await isHeic(file)) {
        return true
      }
    } catch (error) {
      console.warn('Unable to detect HEIC format via signature check:', error)
    }

    const extension = this.fileExtension(file.name)
    return ['heic', 'heif'].includes(extension)
  }

  async convertHeicToJpeg(file) {
    return await heicTo({
      blob: file,
      type: "image/jpeg",
      quality: 0.9
    })
  }

  fileExtension(name) {
    if (!name || !name.includes('.')) {
      return ''
    }
    return name.split('.').pop().toLowerCase()
  }

  convertedFileName(name) {
    const base = name.includes('.') ? name.substring(0, name.lastIndexOf('.')) : name
    const safeBase = base && base.trim().length > 0 ? base : 'converted-image'
    return `${safeBase}.jpg`
  }

  removePhoto(event) {
    const photoIndex = event.currentTarget.dataset.photoIndex
    const wrapper = this.fileInputsContainerTarget.querySelector(`[data-photo-index="${photoIndex}"]`)
    
    if (wrapper) {
      wrapper.remove()
      this.photoCount--
    }
  }

  removeExistingPhoto(event) {
    event.preventDefault()
    const button = event.currentTarget
    const photoId = button.dataset.photoId
    const card = button.closest('.existing-photo-card')
    
    // Find the form element - search in parent elements
    const form = this.element.closest('form')
    if (!form) {
      console.error('Form not found')
      return
    }
    
    // Add hidden input to mark photo for removal
    const hiddenInput = document.createElement('input')
    hiddenInput.type = 'hidden'
    hiddenInput.name = 'remove_photo_ids[]'
    hiddenInput.value = photoId
    form.appendChild(hiddenInput)
    
    // Remove entire column with animation
    const col = card.closest('[class*="col-"]')
    col.style.transition = 'opacity 0.3s'
    col.style.opacity = '0'
    
    setTimeout(() => col.remove(), 300)
  }

  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
  }

  truncateFilename(filename, maxLength) {
    if (filename.length <= maxLength) return filename
    const extension = filename.split('.').pop()
    const nameWithoutExt = filename.substring(0, filename.lastIndexOf('.'))
    const truncated = nameWithoutExt.substring(0, maxLength - extension.length - 4) + '...'
    return truncated + '.' + extension
  }

  escapeHTML(value) {
    const div = document.createElement('div')
    div.textContent = value ?? ''
    return div.innerHTML
  }

  get selectPhotoText() {
    return this.element.dataset.selectPhotoText || 'Выбрать фотографию для загрузки'
  }

  get convertingText() {
    return this.element.dataset.convertingText || 'Конвертация HEIC...'
  }

  get conversionErrorText() {
    return this.element.dataset.conversionErrorText || 'Ошибка конвертации HEIC. Файл будет загружен на сервер.'
  }
}
